// createEditorView.js
// Create a ProseMirror editor view instance

import {EditorView} from "prosemirror-view";
import {EditorState, TextSelection} from "prosemirror-state";

import {editorSchema} from "./schema";
import {createEditorPlugins} from "../plugins/editorPlugins";
import {findNeutralCaretPos} from "../plugins/inlineInteractionPlugin";
import {createLinkEmbedNodeViews} from "./nodeViews/linkEmbedNodeView";
import {UserMentionNodeView, HashtagNodeView} from "./nodeViews/mentionNodeViews";
import {AgenticSectionNodeView} from "./nodeViews/agenticSectionNodeView";
import {FigureNodeView} from "./nodeViews/figureNodeView";
import {MapNodeView} from "../../map/MapNodeView";
import {MapPreviewQueueService} from "../../map/MapPreviewQueueService";
import {openMapEditor} from "../../map/MapSession";
import {getSharedMapFragment} from "../../map/mapFragmentInit";
import {createListNodeView} from "prosemirror-flat-list";
import {withSuggestChanges, isSuggestChangesEnabled} from "@handlewithcare/prosemirror-suggest-changes";

/**
 * Find the highest suggestion id currently in the document so we can issue
 * the next sequential one.  Mirrors the library's internal generateNextNumberId
 * (not exported from the package's public surface).
 */
function nextSuggestionCounter(schema, doc) {
    const insertion = schema.marks.insertion;
    const deletion = schema.marks.deletion;
    const modification = schema.marks.modification;
    let max = 0;
    doc?.descendants((node) => {
        const mark = node.marks.find(
            (m) => m.type === insertion || m.type === deletion || m.type === modification
        );
        if (mark) {
            // id may be "userId:N" (our encoded format) or a plain number
            const raw = mark.attrs.id;
            const n = typeof raw === 'number' ? raw : parseInt(String(raw).split(':').pop(), 10);
            if (Number.isFinite(n) && n > max) max = n;
            // Do NOT return false here — that stops recursion into nested nodes,
            // causing the highest counter in nested structures to be missed and
            // producing duplicate suggestion IDs for content inside list items,
            // agenticSections, etc.
        }
        return true;
    });
    return max + 1;
}

/**
 * Build a custom generateId for withSuggestChanges that encodes the userId into
 * the suggestion id string: "<userId>:<counter>"
 * This lets us display who made each suggestion in the margin rail without any
 * extra data store — the author is recoverable by parsing the id.
 * Falls back to a plain number when no userId is available.
 */
function makeGenerateId(user) {
    const userId = user?.uid ?? user?.id ?? null;
    if (!userId) return undefined; // use library default (plain integer)

    return (schema, doc) => {
        const counter = nextSuggestionCounter(schema, doc);
        return `${userId}:${counter}`;
    };
}

/** Parse a suggestion id back to { userId, counter }.  Works for both the
 *  encoded "userId:N" format and plain numeric ids from the default generator. */
export function parseSuggestionAuthor(id) {
    if (id == null) return {userId: null, isAI: false};
    const str = String(id);
    const colonIdx = str.lastIndexOf(':');
    if (colonIdx === -1) return {userId: null, isAI: false};
    const userId = str.slice(0, colonIdx);
    return {userId, isAI: userId === 'ai'};
}

/**
 * When suggestion mode is active and the user types over a non-empty selection
 * that overlaps a Wikidata entity (or any other meta mark), ProseMirror's
 * readDOMChange produces a wider-than-selection ReplaceStep because it detects
 * mark-structure differences at entity-span boundaries when comparing the old
 * and new DOM. handleTextInput is only invoked by readDOMChange for simple
 * single-text-node changes — it is bypassed entirely for selections that span
 * element boundaries (e.g. entity <span>s), so we cannot fix this there.
 *
 * Fix: intercept at beforeinput, which fires BEFORE the browser modifies the
 * DOM. We call event.preventDefault() so the browser makes no DOM changes,
 * then dispatch a clean tr.insertText(text, sel.from, sel.to) ourselves.
 * readDOMChange therefore sees no DOM mutation and does nothing.
 * withSuggestChanges then receives a clean ReplaceStep(sel.from, sel.to, text)
 * and produces the correct small deletion + insertion marks.
 *
 * We only intercept insertText events with a non-empty selection; all other
 * input types (delete, paste, composition, etc.) are left to ProseMirror.
 */
function makeBeforeInputHandler() {
    return function handleBeforeInput(view, event) {
        // Only handle plain character insertion with a range selection
        if (event.inputType !== 'insertText') return false;
        const text = event.data;
        if (!text) return false;

        const sel = view.state.selection;
        const suggestionActive = isSuggestChangesEnabled(view.state);

        if (!suggestionActive) return false;
        if (sel.empty) return false;

        // Prevent the browser from mutating the DOM, then dispatch clean transaction.
        event.preventDefault();
        view.dispatch(view.state.tr.insertText(text, sel.from, sel.to));
        return true;
    };
}

/**
 * Create a ProseMirror editor view
 * @param {Object} options - Configuration options
 * @param {HTMLElement} options.mount - DOM element to mount the editor
 * @param {string} options.documentId - Document ID for Firestore updates (optional)
 * @param {Object} options.ydoc - Yjs document (optional, for collaboration)
 * @param {Object} options.awareness - Yjs awareness (optional, for collaboration)
 * @param {Object} options.user - Current user object (optional)
 * @param {Object} options.provider - Yjs WebSocket provider (optional, for image uploads)
 * @param {Schema} options.schema - Custom schema (optional, defaults to editorSchema)
 * @param {Object} options.doc - Initial document (optional)
 * @param {Object} options.snippetDrop - Snippet drop plugin options (optional)
 * @param {Function} options.onInlineInteractionChange - Callback when inline interaction target changes (payload or null)
 * @param {boolean} options.inlineInteractionPopovers - Render inline info popovers via ProseMirror plugin views (optional)
 * @param {Function} options.onSelectionChange - Callback when selection state changes (optional)
 * @param {Object} options.yDocMeta - Y.Map reference for document metadata (optional)
 * @param {Object} options.yReferenceMarks - Y.Map("referenceMarks") for UndoManager scope (optional)
 * @param {Object} options.yComments - Y.Map("comments") for UndoManager scope (optional)
 * @returns {EditorView} ProseMirror editor view
 */
export function createEditorView(options) {
    const {
        mount,
        documentId,
        ydoc,
        awareness,
        user,
        provider,
        classificationMenu,
        snippetDrop,
        onInlineInteractionChange,
        inlineInteractionPopovers = false,
        onSelectionChange,
        yDocMeta,
        yReferenceMarks,
        yComments,
        schema = editorSchema,
        doc = null,
        editorMode = 'full',
        getReadOnly = null,
    } = options;

    const yXmlFragment = ydoc?.getXmlFragment("prosemirror");

    // Derive the annotations Y.Map directly from ydoc so FigureNodeView can read
    // and observe saved annotation canvas JSON without prop-drilling through callers.
    const yAnnotations = ydoc ? ydoc.getMap('annotations') : null;

    const state = EditorState.create({
        schema,
        doc,
        plugins: createEditorPlugins({
            yXmlFragment,
            awareness,
            user,
            provider,
            documentId,
            classificationMenu,
            snippetDrop,
            onInlineInteractionChange,
                inlineInteractionPopovers,
            onSelectionChange,
            yDocMeta,
            ydoc,
            yReferenceMarks,
            yComments,
            collaboration: !!(ydoc && awareness),
            editorMode,
            schema,
        }),
    });

    // ponytail (ticket 05): one shared MapPreviewQueueService per editor view — replaces the old
    // one-MapPreviewEngine-per-map-node model. Constructed before the nodeViews map below so the
    // `map` factory can close over it; torn down via the wrapped `editorView.destroy()` further
    // down, alongside the EditorView's own node view teardown.
    const mapPreviewQueue = new MapPreviewQueueService();

    // ponytail: dispatchTransaction uses `this` (bound by ProseMirror via .call(view, tr)).
    // No outer variable needed — avoids TDZ: y-prosemirror dispatches during construction
    // (updatePluginViews → ProsemirrorBinding._forceRerender), before `new EditorView` returns.
    // `let` is still needed because FigureNodeView captures `editorView` via lazy getter below.
    let editorView = new EditorView(mount, {
        state,
        editable: () => !(typeof getReadOnly === 'function' ? getReadOnly() : false),
        nodeViews: {
            ...createLinkEmbedNodeViews(),
            userMention: (node, view, getPos) => new UserMentionNodeView(node, view, getPos),
            hashtag: (node, view, getPos) => new HashtagNodeView(node, view, getPos),
            agenticSection: (node, view, getPos) => new AgenticSectionNodeView(node, view, getPos, {currentUser: user}),
            figure: (node, view, getPos) => new FigureNodeView(node, view, getPos, {classificationMenu, getEditorView: () => editorView, yAnnotations}),
            map: (node, view, getPos) => new MapNodeView(node, view, getPos, {
                classificationMenu,
                // ticket 01 (shared-map-data): every embed windows into the SAME shared
                // fragment — `getFragment` still takes a `nodeId` param (MapNodeView/
                // MapFragmentStore/MapPreviewQueueService keep keying their own per-embed
                // caches/subscriptions/preview jobs by it), but content resolution no longer
                // depends on which node asked.
                getFragment: () => getSharedMapFragment(ydoc),
                onOpenEditor: openMapEditor,
                ydoc,
                yMapPreviews: ydoc ? ydoc.getMap('mapPreviews') : null,
                yMapSitrepGenerations: ydoc ? ydoc.getMap('sitrepGenerations') : null,
                previewQueue: mapPreviewQueue,
            }),
            list: createListNodeView,
        },
        handleDOMEvents: {beforeinput: makeBeforeInputHandler()},
        dispatchTransaction: withSuggestChanges(
            function(tr) { this.updateState(this.state.apply(tr)); },
            makeGenerateId(user)
        ),
    });
    // Expose current user for margin rail components (e.g. SuggestionMarker author label)
    editorView.__currentUser = user ?? null;

    // Tear down the shared preview queue (and its one hidden OL Map) alongside the view itself —
    // ProseMirror's own EditorView.destroy() already destroys every node view (including each
    // MapNodeView, which cancels its own queued/in-flight job), but the queue service instance
    // itself is owned here, not by any single node view, so it needs its own explicit destroy.
    const originalEditorViewDestroy = editorView.destroy.bind(editorView);
    editorView.destroy = function () {
        originalEditorViewDestroy();
        mapPreviewQueue.destroy();
    };

    // Open with no visible caret and no caret-driven inline popovers. Users activate the
    // editor explicitly (click or keyboard); arrow/click onto an entity then opens its card.
    const neutralPos = findNeutralCaretPos(editorView.state.doc);
    const {selection} = editorView.state;
    if (!selection.empty || selection.from !== neutralPos) {
        editorView.dispatch(editorView.state.tr.setSelection(TextSelection.create(editorView.state.doc, neutralPos)));
    }
    if (typeof editorView.dom?.blur === "function") {
        editorView.dom.blur();
    }

    return editorView;
}
